/**
 * DashboardBits — piezas visuales compartidas por los dashboards de la CMDB.
 *
 * Se usan en Servidores y en Máquinas virtuales; el de Equipos mantiene sus
 * propias piezas históricas (wsd-*) para no tocar una página que ya está
 * verificada. Mismas reglas: iconos de @heroicons, cero emojis y colores solo
 * con variables del sistema de diseño.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './DashboardBits.css';

export const COLORS = {
  ok: 'var(--green-600, #16a34a)',
  warn: 'var(--amber-600, #d97706)',
  bad: 'var(--red-600, #dc2626)',
  info: 'var(--accent, #2563eb)',
  muted: 'var(--text-muted, #64748b)',
  gray: 'var(--slate-400, #94a3b8)',
};

export const pct = (value, total) => (total ? Math.round((value / total) * 1000) / 10 : 0);

/** Agrupa por una clave y devuelve [{key, value}] ordenado de mayor a menor. */
export function countBy(items, keyFn) {
  const map = new Map();
  items.forEach((item) => {
    const key = keyFn(item);
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value);
}

export function Tile({ to, tone = 'info', icon: Icon, label, value, hint, bar }) {
  const content = (
    <>
      <div className="dbx-tile-top">
        <span className={`dbx-tile-icon dbx-tone-${tone}`}><Icon /></span>
        <span className="dbx-tile-label">{label}</span>
      </div>
      <div className="dbx-tile-value">{value}</div>
      {typeof bar === 'number' && (
        <div className="dbx-tile-bar">
          <span className={`dbx-fill-${tone}`} style={{ width: `${bar}%` }} />
        </div>
      )}
      {hint && <div className="dbx-tile-hint">{hint}</div>}
    </>
  );
  return to ? (
    <Link to={to} className="dbx-tile">{content}</Link>
  ) : (
    <div className="dbx-tile">{content}</div>
  );
}

export function Donut({ segments, size = 132, thickness = 14, center, label }) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="dbx-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label || ''}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--bg-toolbar)" strokeWidth={thickness} />
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => {
            const length = (segment.value / total) * circumference;
            const dash = `${Math.max(0, length - 1.5)} ${circumference}`;
            const element = (
              <circle
                key={segment.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={thickness}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += length;
            return element;
          })}
      </svg>
      <div className="dbx-donut-center">
        <strong>{center !== undefined ? center : total}</strong>
        {label && <span>{label}</span>}
      </div>
    </div>
  );
}

export function Legend({ items, total }) {
  return (
    <ul className="dbx-legend">
      {items.map((item) => (
        <li key={item.key}>
          <span className="dbx-dot" style={{ background: item.color }} />
          <span className="dbx-legend-label">{item.label}</span>
          <span className="dbx-legend-value">{item.value}</span>
          <span className="dbx-legend-pct">{pct(item.value, total)}%</span>
        </li>
      ))}
    </ul>
  );
}

export function BarRow({ label, value, total, color }) {
  return (
    <div className="dbx-bar-row">
      <span className="dbx-bar-label" title={label}>{label}</span>
      <span className="dbx-bar-track">
        <span style={{ width: `${pct(value, total)}%`, background: color }} />
      </span>
      <span className="dbx-bar-value">{value}</span>
    </div>
  );
}

export function Card({ icon: Icon, tone = 'info', title, subtitle, action, children }) {
  return (
    <section className="dbx-card">
      <header className="dbx-card-head">
        <span className={`dbx-card-icon dbx-tone-${tone}`}><Icon /></span>
        <div className="dbx-card-titles">
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action && <div className="dbx-card-action">{action}</div>}
      </header>
      <div className="dbx-card-body">{children}</div>
    </section>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="dbx-state">
      <Icon />
      <h2>{title}</h2>
      <p>{text}</p>
      {action}
    </div>
  );
}
